import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { firstValueFrom, timeout } from 'rxjs'
import { CompanyInfoPatch, CompanyRegisterLookupError, CompanyRegisterLookupResult } from './company-register.models'

const RPO_API_URL = 'https://api.statistics.sk/rpo/v1/search'
const RPO_REQUEST_TIMEOUT_MS = 30_000

type RpoValue = string | number | null | undefined

interface RpoValidity {
  validFrom?: RpoValue
  validTo?: RpoValue
}

interface RpoTimedValue extends RpoValidity {
  value?: RpoValue
}

interface RpoCodeValue {
  value?: RpoValue
  code?: RpoValue
}

interface RpoAddress extends RpoValidity {
  street?: RpoValue
  regNumber?: RpoValue
  buildingNumber?: RpoValue
  postalCodes?: RpoValue[]
  municipality?: RpoCodeValue
  country?: RpoCodeValue
}

interface RpoEntity {
  identifiers?: RpoTimedValue[]
  fullNames?: RpoTimedValue[]
  addresses?: RpoAddress[]
  termination?: RpoValue
}

interface RpoSearchResponse {
  results?: RpoEntity[]
}

@Injectable({
  providedIn: 'root',
})
export class RpoService {
  constructor(private http: HttpClient) {}

  public async lookupAddress(ico: string | number | null | undefined): Promise<CompanyRegisterLookupResult> {
    const normalizedIco = this.toText(ico)
    if (!/^\d{8}$/.test(normalizedIco)) {
      throw new CompanyRegisterLookupError('invalid-ico')
    }

    try {
      const response = await firstValueFrom(
        this.http
          .get<RpoSearchResponse>(RPO_API_URL, {
            params: new HttpParams().set('identifier', normalizedIco),
          })
          .pipe(timeout({ first: RPO_REQUEST_TIMEOUT_MS }))
      )

      if (!Array.isArray(response?.results)) {
        throw new CompanyRegisterLookupError('unavailable')
      }

      const entity = this.selectEntity(response.results, normalizedIco)
      const status = this.isTerminated(entity, normalizedIco) ? 'terminated' : 'active'
      const values = this.mapEntity(entity)
      if (!values.companyName && !values.street && !values.city && !values.zip) {
        throw new CompanyRegisterLookupError('not-found')
      }

      return { values, status }
    } catch (error) {
      if (error instanceof CompanyRegisterLookupError) {
        throw error
      }
      if (error instanceof HttpErrorResponse) {
        if (error.status === 400) {
          throw new CompanyRegisterLookupError('invalid-ico')
        }
        if (error.status === 404) {
          throw new CompanyRegisterLookupError('not-found')
        }
      }
      throw new CompanyRegisterLookupError('unavailable')
    }
  }

  private selectEntity(results: RpoEntity[], ico: string): RpoEntity {
    const matches = results.filter(entity =>
      (entity.identifiers || []).some(identifier => this.toText(identifier.value) === ico)
    )
    if (!matches.length) {
      throw new CompanyRegisterLookupError('not-found')
    }

    const currentMatches = matches.filter(entity =>
      (entity.identifiers || []).some(
        identifier => this.toText(identifier.value) === ico && this.isCurrentlyValid(identifier)
      )
    )
    if (currentMatches.length === 1) {
      return currentMatches[0]
    }
    if (currentMatches.length > 1 || matches.length > 1) {
      throw new CompanyRegisterLookupError('ambiguous')
    }
    return matches[0]
  }

  private mapEntity(entity: RpoEntity): CompanyInfoPatch {
    const name = this.selectMostRecent(entity.fullNames || [])
    const address = this.selectMostRecent(entity.addresses || [])
    const values: CompanyInfoPatch = {}

    this.setIfPresent(values, 'companyName', name?.value)
    this.setIfPresent(values, 'city', address?.municipality?.value)
    this.setIfPresent(
      values,
      'zip',
      (address?.postalCodes || []).find(value => !!this.toText(value))
    )

    const street = this.formatStreet(address)
    if (street) {
      values.street = street
    }
    if (this.toText(address?.country?.code) === '703') {
      values.country = 'SK'
    }

    return values
  }

  private selectMostRecent<T extends RpoValidity>(records: T[]): T | null {
    if (!records.length) {
      return null
    }
    const current = records.filter(record => this.isCurrentlyValid(record))
    const candidates = current.length ? current : records
    return [...candidates].sort((first, second) =>
      this.toText(second.validFrom).localeCompare(this.toText(first.validFrom))
    )[0]
  }

  private isCurrentlyValid(record: RpoValidity): boolean {
    const today = this.getLocalDate()
    const validFrom = this.toText(record.validFrom).slice(0, 10)
    const validTo = this.toText(record.validTo).slice(0, 10)
    return (!validFrom || validFrom <= today) && (!validTo || validTo >= today)
  }

  private isTerminated(entity: RpoEntity, ico: string): boolean {
    const today = this.getLocalDate()
    const termination = this.toText(entity.termination).slice(0, 10)
    if (termination && termination <= today) {
      return true
    }

    const matchingIdentifiers = (entity.identifiers || []).filter(identifier => this.toText(identifier.value) === ico)
    const hasCurrentIdentifier = matchingIdentifiers.some(identifier => this.isCurrentlyValid(identifier))
    const hasExpiredIdentifier = matchingIdentifiers.some(identifier => {
      const validTo = this.toText(identifier.validTo).slice(0, 10)
      return !!validTo && validTo < today
    })
    return !hasCurrentIdentifier && hasExpiredIdentifier
  }

  private formatStreet(address: RpoAddress | null): string {
    if (!address) {
      return ''
    }
    const street = this.toText(address.street)
    const registrationNumber = this.toText(address.regNumber)
    const buildingNumber = this.toText(address.buildingNumber)
    const usableRegistrationNumber = registrationNumber && registrationNumber !== '0' ? registrationNumber : ''
    const addressNumber =
      usableRegistrationNumber && buildingNumber
        ? `${usableRegistrationNumber}/${buildingNumber}`
        : usableRegistrationNumber || buildingNumber
    return [street, addressNumber].filter(Boolean).join(' ')
  }

  private setIfPresent(values: CompanyInfoPatch, key: keyof CompanyInfoPatch, value: RpoValue): void {
    const text = this.toText(value)
    if (text) {
      values[key] = text
    }
  }

  private getLocalDate(): string {
    const date = new Date()
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  private toText(value: RpoValue): string {
    return value === null || value === undefined ? '' : String(value).trim()
  }
}
